import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, Field, Notice } from '@gatecheckpro/design-system';
import { FailureNotice } from '../lib/refusal.tsx';
import { startCabsFirstUse } from '../lib/cabs.ts';

/**
 * First use: a CABS identity that has no GateCheck account yet.
 *
 * ## Why this page exists rather than a branch in the callback
 *
 * Creating the account needs a display name, and the callback is the wrong place to ask for one:
 * by then the person has already authenticated at CABS, the authorization code is live and short,
 * and a form standing between the two would routinely expire it. So the name is collected first,
 * and the ceremony starts already knowing it.
 *
 * ## What it does not do
 *
 * It does not ask for an email address. The verified address comes from CABS Identity, in the ID
 * token, and asking here would invite somebody to type one they do not own — which is exactly the
 * claim the server refuses (`CONTACT_NOT_VERIFIED`) when the identity's own address is unverified.
 *
 * It also does not sign anybody in to an account that already exists. An identity already bound
 * here is refused `SUBJECT_ALREADY_BOUND`, and the person is told to sign in instead, because
 * "create" quietly behaving as "sign in" is how two accounts become one by accident — or worse,
 * how one person's identity reaches another's account.
 */
export function CabsFirstUsePage() {
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const name = displayName.trim();
    if (name.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await startCabsFirstUse({ displayName: name, nextPath: '/tickets' });
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  }

  return (
    <main className="gc-auth">
      <div className="gc-auth__card gc-stack">
        <div className="gc-stack gc-stack--tight">
          <span className="gc-wordmark">
            Gate<span className="gc-wordmark__accent">Check</span>
          </span>
          <p className="gc-lede">Set up GateCheck with your CABS identity.</p>
        </div>

        <Card>
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <h1>Set up your account</h1>

            <FailureNotice error={error} />

            <Notice tone="info" title="You will sign in at CABS">
              We will send you to CABS Identity to prove who you are. Your GateCheck account is
              created when you come back, and linked to that identity.
            </Notice>

            <Field
              label="Your name"
              name="displayName"
              autoComplete="name"
              required
              hint="What we call you on your tickets and orders."
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />

            <Button type="submit" variant="primary" size="large" block busy={busy}>
              Continue to CABS
            </Button>
          </form>
        </Card>

        <p className="gc-muted">
          Already linked your CABS identity? <Link to="/sign-in">Sign in</Link>
        </p>
      </div>
    </main>
  );
}
